import React, { useEffect, useMemo } from 'react';
import { isEqual } from 'lodash';
import { searchStoreSelector, useSearchStore } from '@warehouse/shared/ui';
import useWorkTypes from '../../../src/utils/hooks/titles/useWorkTypes';
import { ChildrenListPaginationStoreProvider, ChildrenListSearchStoreProvider } from './store-config';
import { ChildrenListFeature } from './ChildrenListFeature';
import { ChildrenListProps } from './types';
import { ChildrenListStoreProvider, useChildrenListStore } from './domain/children.store';
import { childTableStoreSelector } from './domain/children-table.store.selectors';
import getDeepProperty from '../../../src/utils/getDeepProperty';
import { fromBasic } from '../../../src/utils/titleGetProperty';
import { getChildrenTableType, getTitleWorkTypeUuid } from './children-table-type';
import { getChildrenColumnNames } from './useChildrenColumnsPerWorkType';

export function ChildrenList({
	label,
	title,
	searchInputAction,
	relationshipType,
	onChildrenClick,
	includedWorkTypes,
	childWorkType,
	disableVirtualization,
}: ChildrenListProps) {
	const tableType = getChildrenTableType(getTitleWorkTypeUuid(title), childWorkType);
	const sortableColumns = useMemo(() => getChildrenColumnNames(tableType), [tableType]);

	return (
		<ChildrenListStoreProvider tableType={tableType} sortableColumns={sortableColumns}>
			<ChildrenListSearchStoreProvider>
				<ChildrenListPaginationStoreProvider>
					<ChildrenListInner
						label={label}
						title={title}
						searchInputAction={searchInputAction}
						relationshipType={relationshipType}
						onChildrenClick={onChildrenClick}
						includedWorkTypes={includedWorkTypes}
						childWorkType={childWorkType}
						disableVirtualization={disableVirtualization}
					/>
				</ChildrenListPaginationStoreProvider>
			</ChildrenListSearchStoreProvider>
		</ChildrenListStoreProvider>
	);
}

function ChildrenListInner({
	label,
	title,
	searchInputAction,
	relationshipType,
	onChildrenClick,
	includedWorkTypes,
	childWorkType,
	disableVirtualization,
}: ChildrenListProps) {
	const workTypeUuid = useMemo(() => getDeepProperty<string>(title, fromBasic(['workType'])), [title]);
	const workTypes = useWorkTypes();
	const titleStatus = useMemo(
		() => getDeepProperty<string>(title, fromBasic(['terms', 'titleStatus', 'displayValue'])),
		[title],
	);

	const setParentTitle = useChildrenListStore((state) => state.actions.setParentTitle);
	const setSearchValue = useChildrenListStore((state) => state.actions.setSearchValue);

	const parentTitle = useMemo(
		() => ({
			titleUuid: title?.uuid,
			showHiddenTitles: titleStatus === 'hidden',
			relationshipType,
			includedWorkTypes: workTypeUuid === workTypes.compilationUuid() ? undefined : includedWorkTypes,
		}),
		[includedWorkTypes, relationshipType, title?.uuid, titleStatus, workTypeUuid, workTypes],
	);

	const parentTitleFromStore = useChildrenListStore(childTableStoreSelector.parentTitle);
	const searchValueFromStore = useChildrenListStore(childTableStoreSelector.searchValue);

	const searchValue = useSearchStore(searchStoreSelector.searchValue);

	useEffect(() => {
		const updates: Array<[boolean, () => void]> = [
			[!isEqual(parentTitle, parentTitleFromStore), () => setParentTitle(parentTitle)],
			[!isEqual(searchValue, searchValueFromStore), () => setSearchValue(searchValue)],
		];

		updates.forEach(([shouldUpdate, updater]: [boolean, () => void]) => {
			if (shouldUpdate) updater();
		});
	}, [parentTitle, parentTitleFromStore, searchValue, searchValueFromStore, setParentTitle, setSearchValue]);
	return (
		<ChildrenListFeature
			label={label}
			title={title}
			searchInputAction={searchInputAction}
			onChildrenClick={onChildrenClick}
			childWorkType={childWorkType}
			disableVirtualization={disableVirtualization}
		/>
	);
}
